import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Card, CardActions, CardContent, Link, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { IAmendment, IRegulation } from '../../contracts/amendment.contract';
import { IVessel } from '../../contracts/vessel.contract';
import getDateString from '../shared/date-string-convertion';
import { MergeRegulationDescriptionETC } from '../shared';

type RegulationCardsItemProps = {
    regulation: IRegulation;
    expandAll: boolean;

    vessels?: IVessel[];
    riskType?: 'High' | 'Medium' | 'Low' | undefined;
};

const RegulationCardsItem = ({ regulation, expandAll, vessels = undefined }: RegulationCardsItemProps) => {
    const [expand, setExpand] = useState<boolean>(false);

    useEffect(() => {
        setExpand(expandAll);
    }, [expandAll]);

    const renderVessel = (): JSX.Element => {
        if (!vessels || vessels.length === 0) return <></>;

        return (
            <Typography variant="subtitle2" sx={{ color: AbsColor.Blue1, fontWeight: 'bold' }}>
                {vessels.length} {vessels.length === 1 ? 'Vessel' : 'Vessels'}
            </Typography>
        );
    };

    const groupedAmendments = MergeRegulationDescriptionETC(regulation);
    return (
        <Paper elevation={3} sx={{ marginBottom: '32px', width: { xs: '350px', sm: '100%' }, marginLeft: { xs: '12px', sm: '0' } }}>
            <Card>
                <CardContent>
                    {/* Calendar Risk Information */}
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                        {renderVessel()}
                    </Stack>

                    {/* Regulation Name */}
                    <Typography variant="h6" component="div">
                        {regulation?.RegulationName}
                    </Typography>

                    {/* Regulation Document Name and Link */}
                    <CardActions sx={{ padding: '8px 0' }}>
                        <Link href={regulation.Amendments[0].RegulationUrl} underline="always" target="_blank">
                            {regulation?.RegulationDocName}
                        </Link>
                    </CardActions>

                    {/* Regulation Amendments */}
                    <Accordion onChange={(_, expanded) => setExpand(expanded)} expanded={expand} disableGutters sx={{ backgroundColor: AbsColor.White1, marginTop: '24px', boxShadow: 'none' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
                            {regulation?.Amendments.length} {regulation?.Amendments.length === 1 ? 'Amendment' : 'Amendments'}
                        </AccordionSummary>
                        <AccordionDetails>
                            {regulation?.Amendments &&
                                Object.values(groupedAmendments).map((amendmentGroup: IAmendment[]) => (
                                    <RegulationCardsItemAmendments
                                        amendment={amendmentGroup} // Pass the entire amendmentGroup array
                                        expandAll={expandAll}
                                    />
                                ))}
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </Paper>
    );
};

const RegulationCardsItemAmendments = ({ amendment, expandAll }: { amendment: IAmendment[]; expandAll: boolean }) => {
    const [expand, setExpand] = useState<boolean>(false);

    useEffect(() => {
        setExpand(expandAll);
    }, [expandAll]);

    const getAmendmentTitle = (amendmentItem: IAmendment[]): string => {
        const amendmentGroupVesselName = Array.from(new Set(amendmentItem.map((item) => item.ShipType.Name)));
        let vesselName = '';
        if (amendmentGroupVesselName.length === 1) {
            vesselName = amendmentItem[0].ShipType.Name
                ? ['All Vessels', 'All Ships'].includes(amendmentItem[0].ShipType.Name)
                    ? amendmentItem[0].ShipType.Name
                    : `${amendmentItem[0].ShipType.Name}s`
                : 'All Vessels';
        }
        if (amendmentGroupVesselName.length === 2) {
            vesselName = amendmentGroupVesselName.join(' and ');
        }
        if (amendmentGroupVesselName.length > 2) {
            vesselName = amendmentGroupVesselName.join(', ');
        }

        const conditions = getDateString(amendmentItem);
        
        // RPW-11 & RPW-12: If no conditions (GT=0 and all dates are 01-Jan-1900), show "Apply to All Vessels"
        if (!conditions || conditions.trim() === '') {
            return `Apply to ${vesselName}`;
        }

        return `Applies to ${vesselName} with a ${conditions}`;
    };

    return (
        <>
            {amendment && (
                <>
                    <Accordion key={amendment[0].Id} onChange={(_, expanded) => setExpand(expanded)} expanded={expand} disableGutters sx={{ backgroundColor: AbsColor.White1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" sx={{ backgroundColor: AbsColor.Gray1 }}>
                            <Stack direction={{ xs: 'column' }} spacing={{ xs: 1, sm: 2, md: 4 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5}>
                                    <Stack direction="row" spacing={0.5}>
                                        <Typography variant="subtitle2">{amendment[0].OperationalOrHardware === 'OPERATIONAL' ? 'Operational' : 'Hardware'}</Typography>
                                        <Typography
                                            sx={{
                                                color: AbsColor.Black1,
                                                ...(amendment[0].MandatoryOrGuide === 'MANDATORY' && { color: AbsColor.Red1 })
                                            }}
                                            variant="subtitle2"
                                        >
                                            {amendment[0].MandatoryOrGuide === 'MANDATORY' ? 'requirement' : 'guideline'}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="subtitle2">effective {amendment[0].ComplianceDate} -</Typography>
                                    <Typography variant="subtitle2"> applies {amendment[0].AppToAge}</Typography>
                                </Stack>
                                <Typography sx={{ marginTop: '10px !important' }} variant="subtitle2">
                                    {getAmendmentTitle(amendment)}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1" sx={{ fontFamily: "'Maitree', serif", whiteSpace: 'pre-line' }}>
                                {amendment[0].Description}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </>
    );
};

export default RegulationCardsItem;
